/* eslint-disable max-lines */
'use client';

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import toast from 'react-hot-toast';
import { AxiosError } from 'axios';
import { isPhoneValidForForm } from '@components/atoms/ui-kit/phone-input.tsx';

import { usePageHeader } from '@utils/hooks/use-page-header';
import Button from '@components/atoms/ui-kit/button.tsx';
import Modal from '@components/atoms/modals/modal.tsx';
import Input from '@components/atoms/ui-kit/input.tsx';
import ConfirmationModal from '@components/atoms/modals/confirmation-modal.tsx';
import PhoneField from '@components/atoms/ui-kit/phone-input.tsx';
import { Badge } from '@components/atoms/ui-kit/badge.tsx';
import { Text } from '@components/atoms/ui-kit/text.tsx';

import DataGridV2, {
  type DataGridColumn
} from '@components/atoms/datatable/data-grid-v2';
import { GridAction } from '@components/atoms/datatable/grid-action-controller.tsx';

import { useGetResellers } from '@utils/api/resellers/use-get-resellers.tsx';
import { useDeleteCustomer } from '@utils/api/resellers/use-delete-customer.tsx';
import { useCreateResellerCustomer } from '@utils/api/resellers/use-create-reseller-customer.tsx';
import { formatDate } from '@utils/date.ts';
import { PATHS } from '../../router/paths.ts';

import type { BaseError } from '@utils/types/shared/base.ts';
import { ExternalLink, Plus, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

type Row = {
  id: number;
  name: string;
  email: string;
  phone?: string | null;
  status: 'Active' | 'Inactive';
  registration_date: string;
};

type ValidationErr = { loc?: string[]; msg?: string };

type ResellerApiRow = {
  id: number;
  name: string;
  email?: string;
  phone?: string | null;
  is_active?: boolean;
  registration_date?: string;
};

type CreateFormValues = {
  name: string;
  email: string;
  phone?: string | null;
};

function parseError(err: unknown, fallback: string): string {
  const data = (err as AxiosError<BaseError>)?.response?.data;
  if (Array.isArray(data?.detail)) {
    return (data.detail as ValidationErr[])
      .map(e => `${e.loc?.[1] ?? 'field'}: ${e.msg}`)
      .join('\n');
  }
  if (data?.message) return data.message;
  if (data?.error_description) return data.error_description;
  return fallback;
}

export default function Reseller() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: resellers, isLoading, isFetching } = useGetResellers();
  const deleteCustomer = useDeleteCustomer();
  const createCustomer = useCreateResellerCustomer();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedResellerId, setSelectedResellerId] = useState<number | null>(
    null
  );
  const [isDeleting, setIsDeleting] = useState(false);

  /* ---------------- Schema + Form ---------------- */
  const schema = yup.object({
    name: yup.string().required(t('name-is-required')),
    email: yup.string().email().required(t('email-required')),
    phone: yup
      .string()
      .nullable()
      .transform(v => (v?.trim() ? v : undefined))
      .test(
        'is-valid-phone',
        t('enter-a-valid-international-phone-number'),
        isPhoneValidForForm
      )
  });

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    reset
  } = useForm<CreateFormValues>({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: yupResolver(schema) as any
  });

  /* ---------------- Columns ---------------- */
  const columns = useMemo<DataGridColumn<Row>[]>(
    () => [
      {
        id: 'id',
        accessorKey: 'id',
        header: t('id'),
        maxWidth: 90,
        enableSorting: true,
        enableFiltering: true,
        filterVariant: 'text',
        filterKey: 'id',
        serverOperator: 'eq',
        enableRowCopyOnClick: true
      },
      {
        id: 'name',
        accessorKey: 'name',
        header: t('name'),
        minWidth: 160,
        enableSorting: true,
        enableFiltering: true,
        filterVariant: 'text',
        filterKey: 'name',
        serverOperator: 'ilike',
        clampLines: 2,
        cell: ({ getValue }) => (
          <Text as="span" size="sm" weight="medium">
            {String(getValue() ?? '')}
          </Text>
        )
      },
      {
        id: 'email',
        accessorKey: 'email',
        header: t('email'),
        minWidth: 220,
        enableSorting: true,
        enableFiltering: true,
        filterVariant: 'text',
        filterKey: 'email',
        serverOperator: 'ilike',
        enableRowCopyOnClick: false,
        cell: ({ getValue }) => (
          <Text as="span" size="sm">
            {String(getValue() ?? '')}
          </Text>
        )
      },
      {
        id: 'phone',
        accessorKey: 'phone',
        header: t('phone'),
        minWidth: 160,
        maxWidth: 220,
        enableSorting: false,
        enableFiltering: false,
        clampLines: 2,
        cell: ({ getValue }) => {
          const v = String(getValue() ?? '');
          return (
            <Text as="span" size="sm" tone={v ? undefined : 'muted'}>
              {v || '—'}
            </Text>
          );
        }
      },
      {
        id: 'registration_date',
        accessorKey: 'registration_date',
        header: t('registered'),
        minWidth: 160,
        enableSorting: false,
        enableFiltering: false,
        filterVariant: 'date',
        cell: ({ getValue }) => (
          <Text as="span" size="sm">
            {String(getValue() ?? '')}
          </Text>
        )
      },
      {
        id: 'status',
        accessorKey: 'status',
        header: t('status'),
        maxWidth: 140,
        enableSorting: false,
        enableFiltering: false,
        filterVariant: 'select',
        filterOptions: [
          { label: t('all'), value: '' },
          { label: t('active'), value: 'Active' },
          { label: t('inactive'), value: 'Inactive' }
        ],
        cell: ({ getValue }) => {
          const status = String(getValue() ?? '') as 'Active' | 'Inactive';
          return (
            <Badge color={status === 'Active' ? 'green' : 'zinc'}>
              {status === 'Active' ? t('active') : t('inactive')}
            </Badge>
          );
        }
      }
    ],
    [t]
  );

  /* ---------------- Rows ---------------- */
  const rows: Row[] = useMemo(
    () =>
      ((resellers?.result || []) as ResellerApiRow[]).map(r => ({
        id: r.id,
        name: r.name,
        email: String(r.email || '').toLowerCase(),
        phone: r.phone ?? null,
        status: r.is_active ? ('Active' as const) : ('Inactive' as const),
        registration_date: formatDate(r.registration_date || '')
      })),
    [resellers]
  );

  /* ---------------- Create ---------------- */
  const handleCreate = (values: CreateFormValues) => {
    const { name, email, phone } = values;
    setIsSubmitting(true);
    createCustomer
      .mutateAsync({
        name,
        email,
        phone: phone && phone.length > 5 ? phone : undefined,
        is_active: true
      })
      .then(() => {
        toast.success(t('customer-created-successfully'));
        reset();
        setIsModalOpen(false);
      })
      .catch((err: unknown) => {
        toast.error(
          parseError(err, t('error-creating-customer-please-try-again-later'))
        );
      })
      .finally(() => setIsSubmitting(false));
  };

  /* ---------------- Delete ---------------- */
  const handleDelete = () => {
    if (!selectedResellerId) return;

    setIsDeleting(true);
    const toastId = toast.loading(t('deleting-customer'));

    setTimeout(() => {
      deleteCustomer
        .mutateAsync(selectedResellerId)
        .then(() => {
          toast.success(t('customer-deleted-successfully'), { id: toastId });
          setDeleteModal(false);
        })
        .catch((err: unknown) => {
          toast.error(
            parseError(err, t('an-error-occurred-while-deleting-customer')),
            { id: toastId }
          );
        })
        .finally(() => setIsDeleting(false));
    }, 300);
  };

  /* ---------------- Cell Click (open details) ---------------- */
  const handleCellClick = (cellData: Row, field: string) => {
    if (!cellData?.id) return;
    setSelectedResellerId(cellData.id);
    if (['name', 'email'].includes(field)) {
      navigate(PATHS(String(cellData.id)).RESELLER_CUSTOMER);
    }
  };

  /* ---------------- Actions (left click) ---------------- */
  const actions = useMemo<GridAction<Row>[]>(
    () => [
      {
        id: 'view',
        label: t('view'),
        icon: <ExternalLink className="h-4 w-4" />,
        intent: 'primary',
        run: row => {
          navigate(PATHS(String(row.id)).RESELLER_CUSTOMER);
        }
      },
      {
        id: 'delete',
        label: t('delete'),
        icon: <Trash2 className="h-4 w-4" />,
        intent: 'danger',
        run: row => {
          setSelectedResellerId(row.id);
          setDeleteModal(true);
        }
      }
    ],
    [navigate, t]
  );

  /* ---------------- Render ---------------- */
  const showEmptyState =
    !isLoading && !isFetching && rows.length === 0 && !!resellers;

  usePageHeader({
    title: t('reseller-customers'),
    description: t('a-list-of-all-reseller-customers-including-their-details'),
    actions: (
      <Button
        label={t('new-customer')}
        icon={<Plus width={14} />}
        onClick={() => setIsModalOpen(true)}
      />
    )
  });

  return (
    <div className="bg-app min-h-screen">
      {/* Create new reseller customer */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        size="small"
        title={t('create-new-reseller-customer')}
        caption={t(
          'fill-in-the-details-below-to-create-a-new-reseller-customer'
        )}
        footerActions={[
          <Button
            key="cancel"
            onClick={() => setIsModalOpen(false)}
            label={t('cancel')}
            size="small"
            variant="smoke"
            disabled={isSubmitting}
          />,
          <Button
            key="create"
            onClick={handleSubmit(handleCreate)}
            label={isSubmitting ? t('creating') : t('create')}
            size="small"
            variant="primary"
            disabled={isSubmitting}
          />
        ]}
      >
        <form>
          <div className="grid gap-4 w-full px-2">
            <Input
              id="name"
              label={t('name')}
              {...register('name')}
              error={errors.name?.message}
            />
            <Input
              id="email"
              type="email"
              label={t('email')}
              {...register('email')}
              error={errors.email?.message}
            />
            <Controller
              name="phone"
              control={control}
              render={({ field }) => (
                <PhoneField
                  id="phone"
                  label={t('phone')}
                  value={field.value ?? ''}
                  onChange={field.onChange}
                  defaultCountry="NL"
                  placeholder="612345678"
                  error={errors.phone && errors.phone.message}
                  autoComplete="tel"
                  size="medium"
                />
              )}
            />
          </div>
        </form>
      </Modal>

      <div className="px-4 sm:px-0 pb-4 sm:pb-0 relative">
        <DataGridV2<Row>
          data={rows}
          columns={columns}
          actions={actions}
          enablePagination
          manualPagination
          enableSorting
          manualSorting
          enableFiltering
          manualFiltering
          showColumnFilters={false}
          filterMode="popover"
          totalItems={resellers?.total_results ?? 0}
          pageSize={25}
          pageSizeOptions={[25, 50, 75, 100]}
          height="88vh"
          dense
          grid={false}
          striped
          loading={isLoading || isFetching || !resellers}
          getRowId={row => String(row.id)}
          onCellClick={handleCellClick}
        />

        {showEmptyState && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 pointer-events-none">
            <div className="pointer-events-auto flex flex-col items-center gap-3 mt-8">
              <Text as="p" size="sm" tone="muted">
                {t('no-customers-yet')}
              </Text>
              <Button
                label={t('create-first-customer')}
                icon={<Plus width={14} />}
                onClick={() => setIsModalOpen(true)}
                variant="primary"
                size="small"
              />
            </div>
          </div>
        )}
      </div>

      {selectedResellerId !== null && (
        <ConfirmationModal
          isOpen={deleteModal}
          onClose={() => setDeleteModal(false)}
          onConfirm={handleDelete}
          size="small"
          title={t('delete-confirmation')}
          caption={t('are-you-sure-you-want-to-delete-this-customer')}
          confirmLabel={isDeleting ? t('deleting') : t('delete')}
          cancelLabel={t('cancel')}
          confirmDisabled={isDeleting}
          destructive
          autoFocusConfirm
        />
      )}
    </div>
  );
}
